import { apiClient } from '@/lib/api/client'

// ─── Types ──────────────────────────────────────────────
//
// Mirrors easel_prospect_artists (022/024/026). Two suppressions exist and are NOT
// interchangeable: `status === 'suppressed'` is a publishing takedown, `doNotContact` is
// contact consent. Someone may want the page and refuse the email, or the reverse — the UI
// must never collapse them into one "blocked" flag.

export type ProspectStatus =
  | 'candidate' | 'enriched' | 'generated' | 'review' | 'published' | 'claimed' | 'suppressed'

export type OutreachStatus =
  | 'none' | 'queued' | 'contacted' | 'opened' | 'replied' | 'interested'
  | 'not_interested' | 'bounced' | 'unsubscribed' | 'converted'

export interface Prospect {
  id: number
  handle: string
  displayName: string | null
  platform: string
  biography: string | null
  categoryName: string | null
  followers: number | null
  externalLinks: Array<{ url: string; title?: string }> | null
  readinessTier: 'rich' | 'adequate' | 'thin' | 'insufficient' | null
  readinessScore: number | null
  status: ProspectStatus
  email: string | null
  emailSource: string | null
  emailConfidence: number | null
  websiteUrl: string | null
  outreachStatus: OutreachStatus
  firstContactedAt: string | null
  lastContactedAt: string | null
  repliedAt: string | null
  contactAttempts: number
  instantlyLeadId: string | null
  instantlyCampaignId: string | null
  tags: string[]
  notes: string | null
  doNotContact: boolean
  doNotContactReason: string | null
  suppressedReason: string | null
  createdAt: string
  updatedAt: string
}

export interface ProspectContent {
  id: number
  kind: string
  title: string | null
  body: string | null
  url: string | null
  mediaExpired: boolean
  capturedAt: string | null
}

export interface ProspectSection {
  id: number
  position: number
  title: string
  description: string | null
  contentHtml: string
  contentRefs: number[]
  status: string
}

export interface ProspectEvent {
  id: number
  kind: string
  actor: string | null
  detail: Record<string, unknown>
  createdAt: string
}

export interface ProspectStats {
  total: number
  contactable: number
  withEmail: number
  withBio: number
  withGenome: number
  byStatus?: Record<string, number>
  byOutreachStatus?: Record<string, number>
}

export interface ProspectListParams {
  q?: string
  status?: ProspectStatus
  outreachStatus?: OutreachStatus
  tier?: string
  minFollowers?: number
  hasEmail?: boolean
  contactable?: boolean
  hasBio?: boolean
  minCaptions?: number
  tags?: string[]
  sort?: 'readiness' | 'followers' | 'captions' | 'recent'
  limit?: number
  offset?: number
}

export interface ProspectListResponse {
  prospects: Prospect[]
  pagination: { total: number; limit: number; offset: number; hasMore: boolean }
}

export interface ProspectUpdatePayload {
  email?: string | null
  emailSource?: string | null
  websiteUrl?: string | null
  outreachStatus?: OutreachStatus
  tags?: string[]
  notes?: string | null
  status?: Exclude<ProspectStatus, 'suppressed' | 'claimed'>
}

/** Scope is required, never defaulted — see the note on the two suppressions above. */
export interface SuppressPayload {
  scope: 'publishing' | 'contact' | 'both'
  reason: string
}

export interface ProspectClaim {
  id: number
  prospect_id: number
  email: string
  status: 'pending' | 'verified' | 'manual' | 'rejected' | 'expired'
  claim_note: string | null
  created_at: string
  resolved_at: string | null
  resolved_by: string | null
  user_id: number | null
  handle: string
  display_name: string | null
  followers: number | null
  prospect_status: ProspectStatus
}

/**
 * Resolving a MANUAL claim. `email` is required on approve because the manual path exists
 * precisely for artists we hold no address for — a reviewer supplies the one they verified.
 */
export interface ResolveClaimPayload {
  decision: 'approve' | 'reject'
  email?: string
  reason?: string
}

// ─── API Client ─────────────────────────────────────────
//
// Hits the STAFF surface (/api/prospects), which is JWT + MANAGER gated. The service-key
// surface at /api/internal/prospects is for machines; the browser must never hold that key.

export const prospectsApi = {
  getStats: async (): Promise<ProspectStats> => {
    const response = await apiClient.get('/api/prospects/stats')
    return response.data?.data ?? response.data
  },

  getProspects: async (params: ProspectListParams = {}): Promise<ProspectListResponse> => {
    const response = await apiClient.get('/api/prospects', {
      params: {
        ...params,
        tags: params.tags?.length ? params.tags.join(',') : undefined,
        limit: params.limit ?? 50,
        offset: params.offset ?? 0,
      },
    })
    return response.data?.data ?? response.data
  },

  getProspect: async (
    idOrHandle: string | number,
  ): Promise<{ prospect: Prospect; content: ProspectContent[]; sections: ProspectSection[] }> => {
    const response = await apiClient.get(`/api/prospects/${idOrHandle}`)
    return response.data?.data ?? response.data
  },

  getEvents: async (idOrHandle: string | number): Promise<{ events: ProspectEvent[] }> => {
    const response = await apiClient.get(`/api/prospects/${idOrHandle}/events`)
    return response.data?.data ?? response.data
  },

  updateProspect: async (
    idOrHandle: string | number,
    payload: ProspectUpdatePayload,
  ): Promise<{ prospect: Prospect }> => {
    const response = await apiClient.patch(`/api/prospects/${idOrHandle}`, payload)
    return response.data?.data ?? response.data
  },

  getClaims: async (status = 'manual'): Promise<{ claims: ProspectClaim[] }> => {
    const response = await apiClient.get('/api/prospects/claims', { params: { status } })
    return response.data?.data ?? response.data
  },

  resolveClaim: async (
    claimId: number,
    payload: ResolveClaimPayload,
  ): Promise<{ claimId: number; status: string; userId?: number; chapters?: number }> => {
    const response = await apiClient.post(`/api/prospects/claims/${claimId}/resolve`, payload)
    return response.data?.data ?? response.data
  },

  suppress: async (
    idOrHandle: string | number,
    payload: SuppressPayload,
  ): Promise<{ prospect: Prospect }> => {
    const response = await apiClient.post(`/api/prospects/${idOrHandle}/suppress`, payload)
    return response.data?.data ?? response.data
  },
}
